/**
 * Pipeline API service for ATS.
 */

import { createApiClient } from '@aaas/shared';
import type { BulkResultData, BulkResultResponse } from '../types/bulk';
import type {
  Pipeline,
  PipelineListResponse,
  PipelineResponse,
  PipelineCreateInput,
  PipelineUpdateInput,
  PipelineRound,
  PipelineRoundCreateInput,
  PipelineRoundUpdateInput,
  PipelineRoundResponse,
  ReorderRoundsResponse,
} from '../types';

export interface BulkPipelineRoundInput {
  name: string;
  round_type: string;
}

export interface BulkPipelineInput {
  name: string;
  rounds: BulkPipelineRoundInput[];
}

interface ListPipelinesOptions {
  page?: number;
  size?: number;
  sortBy?: string;
  order?: 'asc' | 'desc';
  search?: string;
}

class PipelineService {
  /**
   * List pipelines with pagination.
   */
  async listPipelines(
    workspaceId: string,
    idToken: string,
    options: ListPipelinesOptions = {}
  ): Promise<PipelineListResponse> {
    const { page = 1, size = 10, sortBy = 'created_at', order = 'desc', search } = options;
    const skip = (page - 1) * size;

    const client = createApiClient(idToken, 'ats');
    const response = await client.get<PipelineListResponse>(`/pipelines`, {
      params: { skip, limit: size, sort_by: sortBy, order, search },
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to list pipelines');
    }

    return response.data;
  }

  /**
   * Get a single pipeline by ID.
   */
  async getPipeline(pipelineId: string, workspaceId: string, idToken: string): Promise<Pipeline> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.get<PipelineResponse>(`/pipelines/${pipelineId}`, {
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to get pipeline');
    }

    return response.data.data;
  }

  /**
   * Create a new pipeline.
   */
  async createPipeline(
    workspaceId: string,
    idToken: string,
    data: PipelineCreateInput
  ): Promise<Pipeline> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<PipelineResponse>(`/pipelines`, data, {
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to create pipeline');
    }

    return response.data.data;
  }

  /**
   * Bulk create pipelines (with nested rounds); duplicates skipped silently.
   */
  async bulkCreatePipelines(
    workspaceId: string,
    idToken: string,
    pipelines: readonly BulkPipelineInput[]
  ): Promise<BulkResultData> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<BulkResultResponse>(
      `/pipelines/bulk`,
      { pipelines },
      { headers: { 'X-Workspace-ID': workspaceId } }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to bulk create pipelines');
    }

    return response.data.data;
  }

  /**
   * Update a pipeline.
   */
  async updatePipeline(
    pipelineId: string,
    workspaceId: string,
    idToken: string,
    data: PipelineUpdateInput
  ): Promise<Pipeline> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.patch<PipelineResponse>(`/pipelines/${pipelineId}`, data, {
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update pipeline');
    }

    return response.data.data;
  }

  /**
   * Delete a pipeline.
   */
  async deletePipeline(pipelineId: string, workspaceId: string, idToken: string): Promise<void> {
    const client = createApiClient(idToken, 'ats');
    await client.delete(`/pipelines/${pipelineId}`, {
      headers: { 'X-Workspace-ID': workspaceId },
    });
  }

  // ============ Round Methods ============

  /**
   * Create a new round in a pipeline.
   */
  async createRound(
    pipelineId: string,
    workspaceId: string,
    idToken: string,
    data: PipelineRoundCreateInput
  ): Promise<PipelineRound> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<PipelineRoundResponse>(
      `/pipelines/${pipelineId}/rounds`,
      data,
      { headers: { 'X-Workspace-ID': workspaceId } }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to create round');
    }

    return response.data.data;
  }

  /**
   * Update a round in a pipeline.
   */
  async updateRound(
    pipelineId: string,
    roundId: string,
    workspaceId: string,
    idToken: string,
    data: PipelineRoundUpdateInput
  ): Promise<PipelineRound> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.patch<PipelineRoundResponse>(
      `/pipelines/${pipelineId}/rounds/${roundId}`,
      data,
      { headers: { 'X-Workspace-ID': workspaceId } }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update round');
    }

    return response.data.data;
  }

  /**
   * Delete a round from a pipeline.
   */
  async deleteRound(
    pipelineId: string,
    roundId: string,
    workspaceId: string,
    idToken: string
  ): Promise<void> {
    const client = createApiClient(idToken, 'ats');
    await client.delete(`/pipelines/${pipelineId}/rounds/${roundId}`, {
      headers: { 'X-Workspace-ID': workspaceId },
    });
  }

  /**
   * Reorder rounds in a pipeline.
   */
  async reorderRounds(
    pipelineId: string,
    workspaceId: string,
    idToken: string,
    roundIds: string[]
  ): Promise<PipelineRound[]> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.put<ReorderRoundsResponse>(
      `/pipelines/${pipelineId}/rounds/reorder`,
      { round_ids: roundIds },
      { headers: { 'X-Workspace-ID': workspaceId } }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to reorder rounds');
    }

    return response.data.data;
  }
}

const pipelineService = new PipelineService();
export default pipelineService;
